<template>
  <div
    class="product-pages-wrapper"
    :class="{
      'fixed-product-pages':
        showCreateProduct || showVideoLibrary || showWidgetCreator,
    }"
  >
    <div
      v-if="!isLoading"
      class="products-wrapper"
      :class="{
        'product-segmentation-main-content-wrapper': isProductSegmentation,
      }"
    >
      <div v-if="!isProductSegmentation" class="sub-header">
        <div class="search-filter-wrapper">
          <AutocompletedSearch
            v-if="!hasLargeAmountOfProducts"
            class="products-search"
            :items="products"
            :type="productsString"
            :is-product-pages="true"
            @search="onAutocompletedOptionSelected"
          />
          <ProductsSearchBar
            v-else
            class="products-search"
            :items="products"
            :type="productsString"
            @search="searchOnLargeAmountOfProducts"
          />
          <div
            class="filter-button"
            :class="{ 'selected-filter-button': showFilterModal }"
            @click="openFilterModal"
          >
            <div class="filter-icon">
              <FilterIcon class="filter-svg" />
              <span v-if="numberOfFilters > 0" class="number-filters">
                {{ numberOfFilters }}
              </span>
            </div>
            <p class="filter-by">
              {{ $t("campaignManager.filter.filters") }}
            </p>
          </div>
          <div class="refresh-icon" @click="forceSync">
            <RefreshIcon :is-spinning="isSyncing" />
          </div>
        </div>
        <tippy arrow="true" placement="top" a11y="false">
          <template #trigger>
            <div class="products-with-videos-bar">
              <p>
                {{ $t("productPages.productsWithVideos") }}
                <span>{{ videoPercentage }}%</span>
              </p>
              <div class="bar">
                <div
                  class="filled-bar"
                  :style="{ width: `${videoPercentage}%` }"
                ></div>
              </div>
              <component :is="currentEmote" class="emote" />
            </div>
          </template>
          <div class="emotes-tippy-content">
            <div v-if="hasLargeAmountOfProducts" class="tippy-text">
              <p>{{ $t("productPages.salesInformationLargeAmount") }}</p>
            </div>
            <div v-else class="tippy-text">
              <p>{{ $t("productPages.salesInformation") }}</p>
            </div>
            <div class="emotes-wrapper">
              <div class="emote">
                <CryingEmote />
                <p>0-19%</p>
              </div>
              <div class="emote">
                <SadEmote />
                <p>20-34%</p>
              </div>
              <div class="emote">
                <NeutralEmote />
                <p>35-49%</p>
              </div>
              <div class="emote">
                <HappyEmote />
                <p>50-69%</p>
              </div>
              <div class="emote">
                <PartyEmote />
                <p>70-100%</p>
              </div>
            </div>
          </div>
        </tippy>
      </div>
      <div
        class="product-list-header"
        :class="{
          'product-list-header-segmented-products': isProductSegmentation,
          'product-list-header-localization':
            hasLanguageSettings && !isProductSegmentation,
        }"
      >
        <div
          class="list-element"
          @click="isSortingEnabled ? toggleSort('names') : null"
        >
          <p v-if="!isProductSegmentation" class="list-title">
            {{ $t("productPages.listItems.products") }}
            <span>{{ productListCountData }}</span>
          </p>
          <p v-else class="list-title">
            {{ $t("productPages.listItems.productsSegmentation") }}
          </p>
          <div
            v-if="!isSortingEnabled"
            v-tippy="{ placement: 'top', arrow: true }"
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
            :content="$t('productPages.sortTooltip')"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'names' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'names' && selectedArrow === -1,
              }"
            />
          </div>

          <div
            v-else
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'names' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'names' && selectedArrow === -1,
              }"
            />
          </div>
        </div>
        <div
          v-if="hasLanguageSettings && !isProductSegmentation"
          class="list-element"
        >
          <p class="list-title">Locales</p>
        </div>
        <div
          class="list-element"
          @click="isSortingEnabled ? toggleSort('created') : null"
        >
          <p class="list-title">
            {{ $t("productPages.listItems.created") }}
          </p>
          <div
            v-if="!isSortingEnabled"
            v-tippy="{ placement: 'top', arrow: true }"
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
            :content="$t('productPages.sortTooltip')"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'created' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'created' && selectedArrow === -1,
              }"
            />
          </div>
          <div
            v-else
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'created' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'created' && selectedArrow === -1,
              }"
            />
          </div>
        </div>
        <div
          v-if="!isProductSegmentation"
          class="list-element"
          @click="isSortingEnabled ? toggleSort('quantity') : null"
        >
          <p class="list-title">
            {{ $t("productPages.sortingOptions.quantity") }}
          </p>
          <div
            v-if="!isSortingEnabled"
            v-tippy="{ placement: 'top', arrow: true }"
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
            :content="$t('productPages.sortTooltip')"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'quantity' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'quantity' && selectedArrow === -1,
              }"
            />
          </div>
          <div
            v-else
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'quantity' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'quantity' && selectedArrow === -1,
              }"
            />
          </div>
        </div>
        <div
          class="list-element"
          @click="isSortingEnabled ? toggleSort('status') : null"
        >
          <p class="list-title">
            {{ $t("productPages.listItems.status") }}
          </p>
          <div
            v-if="!isSortingEnabled"
            v-tippy="{ placement: 'top', arrow: true }"
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
            :content="$t('productPages.sortTooltip')"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'status' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'status' && selectedArrow === -1,
              }"
            />
          </div>
          <div
            v-else
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'status' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'status' && selectedArrow === -1,
              }"
            />
          </div>
        </div>
        <div
          class="list-element"
          @click="isSortingEnabled ? toggleSort('lastImport') : null"
        >
          <p class="list-title">
            {{ $t("productPages.listItems.lastImport") }}
          </p>
          <div
            v-if="!isSortingEnabled"
            v-tippy="{ placement: 'top', arrow: true }"
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
            :content="$t('productPages.sortTooltip')"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'lastImport' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'lastImport' && selectedArrow === -1,
              }"
            />
          </div>
          <div
            v-else
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'lastImport' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'lastImport' && selectedArrow === -1,
              }"
            />
          </div>
        </div>
        <div
          class="list-element"
          @click="isSortingEnabled ? toggleSort('widget') : null"
        >
          <p class="list-title">
            {{ $t("productPages.listItems.widget") }}
          </p>
          <div
            v-if="!isSortingEnabled"
            v-tippy="{ placement: 'top', arrow: true }"
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
            :content="$t('productPages.sortTooltip')"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'widget' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'widget' && selectedArrow === -1,
              }"
            />
          </div>
          <div
            v-else
            class="sorting-arrows"
            :class="{ 'disabled-sorting': !isSortingEnabled }"
          >
            <SortArrow
              :class="{
                'selected-arrow':
                  selectedSorting === 'widget' && selectedArrow === 1,
              }"
            />
            <SortArrow
              class="reverse-arrow"
              :class="{
                'selected-arrow':
                  selectedSorting === 'widget' && selectedArrow === -1,
              }"
            />
          </div>
        </div>
      </div>
      <div
        ref="scrollContainer"
        class="product-cards-container"
        :class="{ 'larger-margin': !showBottomButtons }"
      >
        <div
          v-if="!hasProducts && !isLoading && !hasActiveFilters"
          class="no-products"
        >
          <div class="no-products-image">
            <img src="@/assets/sitting-woman.png" alt="sitting-woman" />
          </div>
          <div class="no-products-text">
            <p>{{ $t("productPages.noProductsInstructions.1") }}</p>
            <div class="second-sentence">
              <p>
                {{ $t("productPages.noProductsInstructions.2") }}
              </p>
              <p class="bold">
                {{ $t("productPages.noProductsInstructions.3") }}
              </p>
              <p>
                {{ $t("productPages.noProductsInstructions.4") }}
              </p>
              <p class="bold">
                {{ $t("productPages.noProductsInstructions.5") }}
              </p>
              <p>{{ $t("productPages.noProductsInstructions.6") }}</p>
            </div>
          </div>
        </div>
        <div
          v-for="{
            creationDate,
            imageUrl,
            name,
            variants,
            _id,
            videosAttached,
            formatsAttached,
            productId,
            videoLastImportDate,
            productKey,
            productUrl,
          } in productsToIterate"
          v-else
          :key="_id"
          :ref="_id"
          class="product-cards"
        >
          <template v-if="!expandedProducts[_id]">
            <div
              class="product-card"
              :class="{
                'product-card-product-segmentation': isProductSegmentation,
                'product-card-localization':
                  hasLanguageSettings && !isProductSegmentation,
              }"
            >
              <div
                v-tippy="{ placement: 'top', arrow: true }"
                class="image-name"
                :content="name"
              >
                <div
                  class="product-img-wrapper"
                  @mouseenter="handleMouseEnter(_id)"
                  @mouseleave="handleMouseLeave"
                >
                  <img
                    v-lazy="imageUrl"
                    class="product-img"
                    alt="product-img"
                  />
                  <div
                    v-if="productUrl"
                    :class="[
                      'product-link',
                      { visible: hoveredProductId === _id },
                    ]"
                  >
                    <a :href="productUrl" target="_blank"
                      ><NewTabIcon class="new-tab-icon"
                    /></a>
                  </div>
                </div>
                <div class="name-info">
                  <h2 class="product-name">{{ truncateName(name) }}</h2>
                  <div class="product-info-flex">
                    <p class="product-info">
                      {{ firstVariantPrice(variants) }}
                    </p>
                    <p v-if="isProductSegmentation" class="product-info">
                      &nbsp;- {{ calculateTotalQty(variants) }}
                    </p>
                  </div>
                </div>
              </div>
              <div
                v-if="hasLanguageSettings && !isProductSegmentation"
                class="product-language"
                @click="toggleLanguageExpansion(_id)"
              >
                <div class="globe-wrapper">
                  <img src="@/assets/globe-icon.png" alt="globe" />
                  <ArrowDownBig class="arrow-down-big" />
                </div>
              </div>
              <p class="product-creation-date">
                {{ formattedDate(creationDate) }}
              </p>
              <p v-if="!isProductSegmentation" class="product-creation-date">
                {{ calculateTotalQty(variants) }}
              </p>
              <div class="status">
                <p
                  class="number-of-videos"
                  :class="{
                    'there-are-videos': (videosAttached?.length || 0) > 0,
                  }"
                >
                  <span>{{ videosAttached?.length || 0 }}</span>
                  {{ $t("account.usage.videos") }}
                </p>
                <div
                  v-if="!isProductSegmentation"
                  @click="
                    addOrManageVideos(
                      formatsAttached,
                      videosAttached,
                      productId,
                      productKey
                    )
                  "
                >
                  <p
                    v-if="
                      videosAttached &&
                      videosAttached.length > 0 &&
                      formatsAttached &&
                      formatsAttached.length > 0
                    "
                    class="add-manage"
                  >
                    {{ $t("productPages.manageVideos") }}
                  </p>
                  <p
                    v-else
                    class="add-manage"
                    :class="{ 'disabled-button': !formatsAttached }"
                  >
                    {{ $t("productPages.addVideos") }}
                  </p>
                </div>
              </div>
              <p class="last-import">
                {{ safeFormattedDate(videoLastImportDate) }}
              </p>
              <div class="widgets">
                <div
                  v-if="formatsAttached && formatsAttached.length > 0"
                  class="widgets-images"
                >
                  <template v-for="format in uniqueFormats(formatsAttached)">
                    <tippy
                      :key="format.campaignId"
                      arrow="true"
                      placement="top"
                      a11y="false"
                      interactive="true"
                      max-width="500px"
                    >
                      <template #trigger>
                        <div class="widget-icon-wrapper">
                          <component
                            :is="getWidgetComponent(format.formatName)"
                            class="widget-icon"
                          />
                          <WarningIcon
                            v-if="format.notEmbedded && !isProductSegmentation"
                            class="warning-icon"
                          />
                        </div>
                      </template>
                      <div>
                        <div
                          v-if="format.notEmbedded && !isProductSegmentation"
                          class="tippy-content"
                        >
                          <p>
                            {{ $t("productPages.noViews") }}
                          </p>
                          <div
                            class="see-embed-code"
                            @click="openEmbedCodeModal(format.campaignId)"
                          >
                            {{ $t("productPages.seeEmbed") }}
                          </div>
                          <button class="button-primary">
                            {{ $t("productPages.check") }}
                          </button>
                        </div>
                        <div v-else class="tippy-content standard-tippy">
                          <div class="tippy-content-section tippy-top-block">
                            <p class="format-type">
                              {{ getWidgetFormat(format.formatName) }}
                            </p>
                            |
                            <p class="tooltip-campaign-name">
                              "{{ format.campaignName }}"
                            </p>
                          </div>
                          <div
                            v-if="!isProductSegmentation"
                            class="tippy-content-section tippy-bottom-block"
                          >
                            <div
                              class="see-embed-code"
                              @click="openWidgetFormat(format.campaignId)"
                            >
                              <PlayIcon class="tooltip-icons" />
                              {{ $t("productPages.widgetSettings") }}
                            </div>
                            <div
                              v-if="
                                format.formatName === 'carousel' ||
                                format.formatName === 'story' ||
                                format.formatName === 'embed'
                              "
                              class="see-embed-code"
                              @click="openEmbedCodeModal(format.campaignId)"
                            >
                              <CodeIcon class="tooltip-icons" />
                              {{ $t("productPages.seeEmbed") }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </tippy>
                  </template>
                </div>
                <div
                  v-if="
                    (!formatsAttached ||
                      formatsAttached.length < bulkCampaigns.length) &&
                    !isProductSegmentation
                  "
                  class="no-widget"
                  @click="
                    addWidget(
                      productId,
                      formatsAttached,
                      videosAttached,
                      productKey,
                      name
                    )
                  "
                >
                  <p>+</p>
                </div>
              </div>
              <button
                v-if="!isProductSegmentation"
                class="option-button"
                @click="openOptionsModal(_id)"
              >
                <img
                  src="../assets/campaign-options-button.png"
                  alt="options-button"
                />
              </button>
              <SquaredX
                v-if="isProductSegmentation"
                class="delete-icon"
                @click="removeProductSegmentation(productId)"
              />
            </div>
          </template>
          <template v-else>
            <div
              v-for="(langCode, idx) in languageCodes"
              :key="_id + '-' + langCode"
            >
              <div
                class="product-card product-localization-pink-background"
                :class="{
                  'product-card-product-segmentation': isProductSegmentation,
                  'product-card-localization': hasLanguageSettings,
                }"
              >
                <div
                  v-tippy="{ placement: 'top', arrow: true }"
                  class="image-name"
                  :content="name"
                >
                  <div
                    class="product-img-wrapper"
                    @mouseenter="handleMouseEnter(_id)"
                    @mouseleave="handleMouseLeave"
                  >
                    <img
                      v-lazy="imageUrl"
                      class="product-img"
                      alt="product-img"
                    />
                    <div
                      v-if="productUrl"
                      :class="[
                        'product-link',
                        { visible: hoveredProductId === _id },
                      ]"
                    >
                      <a :href="productUrl" target="_blank"
                        ><NewTabIcon class="new-tab-icon"
                      /></a>
                    </div>
                  </div>
                  <div class="name-info">
                    <h2 class="product-name">{{ truncateName(name) }}</h2>
                    <div class="product-info-flex">
                      <p class="product-info">
                        {{ firstVariantPrice(variants) }}
                      </p>
                      <p v-if="isProductSegmentation" class="product-info">
                        &nbsp;- {{ calculateTotalQty(variants) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="list-element">
                  <div
                    class="globe-wrapper"
                    :class="{ 'globe-wrapper-without-hover': idx > 0 }"
                    @click="idx === 0 && toggleLanguageExpansion(_id)"
                  >
                    <img
                      class="language-flag"
                      :src="getLocaleFlag(langCode)"
                      alt="flag"
                    />
                    <div v-if="idx === 0" class="language-toggle-icon">
                      <ArrowUpBig class="arrow-down-big" />
                    </div>
                  </div>
                </div>
                <p class="product-creation-date">
                  {{ formattedDate(creationDate) }}
                </p>
                <p v-if="!isProductSegmentation" class="product-creation-date">
                  {{ calculateTotalQty(variants) }}
                </p>
                <div class="status">
                  <p
                    class="number-of-videos"
                    :class="{
                      'there-are-videos':
                        countVideosForLang(productId, productKey, langCode) > 0,
                    }"
                  >
                    <span>{{
                      countVideosForLang(productId, productKey, langCode)
                    }}</span>
                    {{ $t("account.usage.videos") }}
                  </p>

                  <div
                    v-if="!isProductSegmentation"
                    @click="
                      addOrManageVideos(
                        getFormatsForLang(
                          formatsAttached,
                          productId,
                          productKey,
                          langCode
                        ),
                        videosAttached,
                        productId,
                        productKey,
                        langCode
                      )
                    "
                  >
                    <p
                      v-if="
                        getFormatsForLang(
                          formatsAttached,
                          productId,
                          productKey,
                          langCode
                        ).length > 0 ||
                        (videosAttached && videosAttached.length > 0)
                      "
                      class="add-manage"
                    >
                      {{ $t("productPages.manageVideos") }}
                    </p>

                    <p
                      v-else
                      class="add-manage"
                      :class="{
                        'disabled-button':
                          getFormatsForLang(
                            formatsAttached,
                            productId,
                            productKey,
                            langCode
                          ).length === 0,
                      }"
                    >
                      {{ $t("productPages.addVideos") }}
                    </p>
                  </div>
                </div>
                <p class="last-import">
                  {{ safeFormattedDate(videoLastImportDate) }}
                </p>
                <div class="widgets">
                  <div
                    v-if="
                      getFormatsForLang(
                        formatsAttached,
                        productId,
                        productKey,
                        langCode
                      ).length > 0
                    "
                    class="widgets-images"
                  >
                    <template
                      v-for="format in uniqueFormats(
                        getFormatsForLang(
                          formatsAttached,
                          productId,
                          productKey,
                          langCode
                        )
                      )"
                    >
                      <tippy
                        :key="format.campaignId"
                        arrow="true"
                        placement="top"
                        a11y="false"
                        interactive="true"
                        max-width="500px"
                      >
                        <template #trigger>
                          <div class="widget-icon-wrapper">
                            <component
                              :is="getWidgetComponent(format.formatName)"
                              class="widget-icon"
                            />
                            <WarningIcon
                              v-if="
                                format.notEmbedded && !isProductSegmentation
                              "
                              class="warning-icon"
                            />
                          </div>
                        </template>
                        <div>
                          <div
                            v-if="format.notEmbedded && !isProductSegmentation"
                            class="tippy-content"
                          >
                            <p>{{ $t("productPages.noViews") }}</p>
                            <div
                              class="see-embed-code"
                              @click="openEmbedCodeModal(format.campaignId)"
                            >
                              {{ $t("productPages.seeEmbed") }}
                            </div>
                            <button class="button-primary">
                              {{ $t("productPages.check") }}
                            </button>
                          </div>
                          <div v-else class="tippy-content standard-tippy">
                            <div class="tippy-content-section tippy-top-block">
                              <p class="format-type">
                                {{ getWidgetFormat(format.formatName) }}
                              </p>
                              |
                              <p class="tooltip-campaign-name">
                                "{{ format.campaignName }}"
                              </p>
                            </div>
                            <div
                              v-if="!isProductSegmentation"
                              class="tippy-content-section tippy-bottom-block"
                            >
                              <div
                                class="see-embed-code"
                                @click="openWidgetFormat(format.campaignId)"
                              >
                                <PlayIcon class="tooltip-icons" />
                                {{ $t("productPages.widgetSettings") }}
                              </div>
                              <div
                                v-if="
                                  format.formatName === 'carousel' ||
                                  format.formatName === 'story' ||
                                  format.formatName === 'embed'
                                "
                                class="see-embed-code"
                                @click="openEmbedCodeModal(format.campaignId)"
                              >
                                <CodeIcon class="tooltip-icons" />
                                {{ $t("productPages.seeEmbed") }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </tippy>
                    </template>
                  </div>

                  <div
                    v-if="
                      hasRoomForMoreWidgets(
                        formatsAttached,
                        productId,
                        productKey,
                        langCode
                      ) && !isProductSegmentation
                    "
                    class="no-widget"
                    @click="
                      addWidget(
                        productId,
                        getFormatsForLang(
                          formatsAttached,
                          productId,
                          productKey,
                          langCode
                        ),
                        videosAttached,
                        productKey,
                        name,
                        langCode
                      )
                    "
                  >
                    <p>+</p>
                  </div>
                </div>
                <button
                  v-if="!isProductSegmentation"
                  class="option-button"
                  @click="openOptionsModal(_id)"
                >
                  <img
                    src="../assets/campaign-options-button.png"
                    alt="options-button"
                  />
                </button>
                <SquaredX
                  v-if="isProductSegmentation"
                  class="delete-icon"
                  @click="removeProductSegmentation(productId)"
                />
              </div>
            </div>
          </template>
          <ProductOptionsModal
            v-if="showOptionsModal && selectedProductId === _id"
            :product="selectedProductObject"
            @close-options-modal="closeOptionsModal"
            @edit-product="editProduct"
            @open-delete-modal="openDeleteModal"
            @open-product-properties-modal="openProductPropertiesModal"
          />
        </div>
        <div class="animation-container">
          <div v-if="isLoadingMore" class="lds-ring">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        </div>
      </div>
      <div
        v-if="showBottomButtons && !isProductSegmentation"
        class="bottom-buttons"
      >
        <button
          class="main-button first-button"
          @click="openCreateProductModal"
        >
          <CreateIcon />{{ $t("productPages.topButtons.create") }}
        </button>
        <button class="main-button second-button">
          <UploadIcon />{{ $t("productPages.topButtons.import") }}
        </button>
      </div>
      <transition name="modal-slide">
        <CreateProductSlidingModal
          v-if="showCreateProduct"
          :edit-mode="editMode"
          :edit-product-data="editProductData"
          @close-elements-modal="closeCreateProduct"
        />
      </transition>
      <DeleteProductModal
        v-if="showDeleteModal"
        :product-to-delete="productToDelete"
        @close="closeDeleteModal"
        @delete-product="deleteProduct"
      />
    </div>
    <EmbedCodeModal
      v-if="showEmbedCodeModal"
      :campaign="selectedWidget"
      @close="closeEmbedCodeModal"
    />
    <transition name="modal-slide">
      <elements-sliding-modal
        v-if="showStep3Modal"
        :current-feed-edit-index="currentFeedEditIndex"
        :selected-current-feed-edit="selectedCurrentFeedEdit"
        :is-edit-bulk-format="false"
        :is-product-pages="true"
        :campaign-id="campaignIdForStep3"
        :selected-product-id="selectedProductId"
        :comes-from-step-one="comesFromStepOne"
        :videos-attached-on-product="videosAttachedOnProduct"
        @update-product="updateProductVideosAttached"
        @update-current-feed="onFeedSaved"
        @close-elements-modal="closeStep3Modal"
      >
      </elements-sliding-modal>
    </transition>
    <transition name="modal-slide">
      <elements-sliding-modal
        v-if="showWidgetFormatModal"
        :current-feed-edit-index="0"
        :is-edit-bulk-format="true"
        :is-bulk="true"
        :campaign-id="campaignIdForStep3"
        @close-elements-modal="closeWidgetFormalModal"
      >
      </elements-sliding-modal>
    </transition>
    <transition name="modal-slide">
      <WidgetCreatorSlidingModal
        v-if="isWidgetCreatorOpened"
        :comes-from-product-pages="true"
        :product-id-selected="productIdSelected"
        :product-name-selected="productNameSelected"
        @close="closeWidgetCreator"
      />
    </transition>
    <SelectWidgetModal
      v-if="openAddVideosToWidgetModal"
      :possible-widgets="possibleWidgets"
      :is-add-widget="isAddWidget"
      :selected-product-id="selectedProductId"
      @close="closeAddVideosToWidgetModal"
      @widget-selected="addVideosToWidget"
    />
    <SpinnerLoader v-if="isFetching" />
    <SpinnerLoader
      v-if="loadingLargeAmountProducts || isLoading"
      :is-large-amount-of-products="true"
      :big-padding="true"
      @stop-search="stopSearch"
    />
    <ProductsFilter
      v-if="showFilterModal"
      :categories-options="productUniqueValues['category']"
      :collections-options="productUniqueValues['collections']"
      :brands-options="productUniqueValues['brand']"
      :languages-options="languages"
      :selected-categories="selectedFilters.categories"
      :selected-collections="selectedFilters.collections"
      :selected-brands="selectedFilters.brands"
      :selected-has-videos="selectedFilters.hasVideos"
      :selected-has-stock="selectedFilters.hasStock"
      :selected-status="selectedFilters.productStatus"
      @close-filter-modal="showFilterModal = false"
      @filter="searchOnLargeAmountOfProducts"
      @video-filter="filterProductsWithVideos"
      @stock-filter="filterProductsWithStock"
      @status-filter="filterProductsWithStatus"
      @reset="resetFilters"
    />
    <ProductPropertiesModal
      v-if="showProductPropertiesModal"
      :product="productForPropertiesModal"
      @close="showProductPropertiesModal = false"
    />
  </div>
</template>

<script>
import SortArrow from "@/assets/svg/sort-arrow.svg?inline";
import UploadIcon from "@/assets/svg/upload-icon.svg?inline";
import CreateIcon from "@/assets/svg/create-icon.svg?inline";
import CryingEmote from "@/assets/svg/crying-emote.svg?inline";
import SadEmote from "@/assets/svg/sad-emote.svg?inline";
import NeutralEmote from "@/assets/svg/neutral-emote.svg?inline";
import HappyEmote from "@/assets/svg/happy-emote.svg?inline";
import PartyEmote from "@/assets/svg/party-emote.svg?inline";
import BubbleWidget from "@/assets/svg/bubble-widget.svg?inline";
import CarouselWidget from "@/assets/svg/carousel-widget.svg?inline";
import StoriesWidget from "@/assets/svg/stories-widget.svg?inline";
import PopupWidget from "@/assets/svg/popup-widget.svg?inline";
import EmbedWidget from "@/assets/svg/embed-widget.svg?inline";
import WarningIcon from "@/assets/svg/warning-icon.svg?inline";
import PlayIcon from "@/assets/svg/small-play-icon.svg?inline";
import CodeIcon from "@/assets/svg/embed-code-icon.svg?inline";
import NewTabIcon from "@/assets/svg/open_new_tab.svg?inline";
import CloseIcon from "@/assets/svg/settings/close.svg?inline";
import SquaredX from "@/assets/svg/squared-x-icon.svg?inline";
import ArrowDownBig from "@/assets/svg/arrow-down-big.svg?inline";
import ArrowUpBig from "@/assets/svg/arrow-up-big.svg?inline";

import AutocompletedSearch from "./shared/AutocompletedSearch.vue";
import ProductsSearchBar from "./shared/ProductsSearchBar.vue";
import VidjetHeader from "./shared/Vidjet-header";
import ProductOptionsModal from "@/components/ProductOptionsModal.vue";
import SpinnerLoader from "./shared/SpinnerLoader.vue";
import CreateProductSlidingModal from "@/components/CreateProductSlidingModal.vue";
import DeleteProductModal from "@/components/DeleteProductModal.vue";
import VideoLibrarySlidingModal from "@/components/shared/VideoLibrarySlidingModal.vue";
import EmbedCodeModal from "@/components/campaign-manager/EmbedCodeModal.vue";
import ElementsSlidingModal from "@/components/create-campaign-steps/ElementsSlidingModal.vue";
import SelectWidgetModal from "@/components/SelectWidgetModal.vue";
import RefreshIcon from "./shared/RefreshIcon.vue";
import FilterIcon from "@/assets/svg/filter-icon.svg?inline";
import ProductsFilter from "./shared/ProductsFilter.vue";
import ProductPropertiesModal from "./shared/ProductPropertiesModal.vue";

import { mapGetters, mapState } from "vuex";
import enums from "../enums";
const { FormatType } = enums;
import { locales } from "@/utils/locales";

const WidgetCreatorSlidingModal = () =>
  import("@/components/create-campaign-steps/WidgetCreatorSlidingModal.vue");

export default {
  components: {
    AutocompletedSearch,
    SortArrow,
    UploadIcon,
    CreateIcon,
    ProductOptionsModal,
    SpinnerLoader,
    CreateProductSlidingModal,
    DeleteProductModal,
    CryingEmote,
    SadEmote,
    NeutralEmote,
    HappyEmote,
    PartyEmote,
    VideoLibrarySlidingModal,
    BubbleWidget,
    CarouselWidget,
    StoriesWidget,
    EmbedCodeModal,
    ElementsSlidingModal,
    SelectWidgetModal,
    WarningIcon,
    RefreshIcon,
    PlayIcon,
    CodeIcon,
    PopupWidget,
    EmbedWidget,
    WidgetCreatorSlidingModal,
    NewTabIcon,
    CloseIcon,
    VidjetHeader,
    SquaredX,
    ProductsSearchBar,
    FilterIcon,
    ProductsFilter,
    ArrowDownBig,
    ArrowUpBig,
    ProductPropertiesModal,
  },
  props: {
    campaignId: {
      type: String,
      default: null,
    },
    isProductSegmentation: {
      type: Boolean,
      default: false,
    },
    segmentedProducts: {
      type: Array,
      default: () => [],
    },
    showVideoLibrary: {
      type: Boolean,
      default: false,
    },
    showWidgetCreator: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedProductId: null,
      selectedSorting: "created",
      selectedArrow: -1,
      showOptionsModal: false,
      selectedProductObject: null,
      isLoading: false,
      showCreateProduct: false,
      editMode: false,
      editProductData: null,
      showDeleteModal: false,
      productToDelete: null,
      showEmbedCodeModal: false,
      selectedWidget: null,
      showStep3Modal: false,
      possibleWidgets: [],
      openAddVideosToWidgetModal: false,
      campaignIdForStep3: null,
      isAddWidget: false,
      selectedCurrentFeedEdit: null,
      videoLibraryCreation: false,
      currentFeedEditIndex: null,
      isFetching: false,
      showWidgetFormatModal: false,
      isWidgetCreatorOpened: false,
      productIdSelected: null,
      productNameSelected: null,
      hoveredProductId: null,
      isSyncing: false,
      videosAttachedOnProduct: [],
      lastEditedProduct: null,
      showFilterModal: false,
      languages: ["English", "Spanish", "French", "German", "Chinese"],
      visibleProducts: 10,
      isLoadingMore: false,
      hasActiveFilters: false,
      selectedFilters: {
        categories: [],
        collections: [],
        brands: [],
        productName: "",
        hasVideos: null,
        hasStock: null,
        productStatus: [],
      },
      loadingLargeAmountProducts: false,
      isAborted: false,
      locales,
      expandedProducts: {},
      pendingLangCode: null,
      // cachedProducts is to save the products filtered so we keep the filtered list after open elements sliding modal
      cachedProducts: null,
      productForPropertiesModal: null,
      showProductPropertiesModal: false,
      feedWasSaved: false,
      createdFeedWasNew: false,
    };
  },
  computed: {
    ...mapState(["site"]),
    ...mapState({
      products: (state) => state.product.products,
      productsCount: (state) => state.product.productsCount,
      productsSearchCount: (state) => state.product.productsSearchCount,
      productUniqueValues: (state) => state.product.productUniqueValues,
      hasLargeAmountOfProducts: (state) =>
        state.product.hasLargeAmountOfProducts,
      campaigns: (state) => state.campaign.campaigns || [],
    }),
    ...mapGetters({
      siteId: "account/getSiteId",
      currency: "site/getRawCurrency",
      getCurrency: "site/getCurrency",
      singleCampaign: "campaign/getSingle",
    }),
    isShopify() {
      return this.site.site.integration === "shopify";
    },
    hasProducts() {
      if (this.isProductSegmentation) return true;
      return this.products.length > 0;
    },
    productsString() {
      return this.$t("productPages.products");
    },
    videoPercentage() {
      // % of visible products that have at least one attached video
      if (!this.productsToIterate || this.productsToIterate.length === 0) {
        return 0;
      }

      const productsWithVideos = this.productsToIterate.filter(
        (product) => product.videosAttached && product.videosAttached.length > 0
      );

      const percentage =
        (productsWithVideos.length / this.productsToIterate.length) * 100;
      return Math.round(percentage);
    },
    currentEmote() {
      // Map percentage to an emoji component
      const percentage = this.videoPercentage;
      if (percentage <= 19) {
        return "CryingEmote";
      } else if (percentage <= 34) {
        return "SadEmote";
      } else if (percentage <= 49) {
        return "NeutralEmote";
      } else if (percentage <= 69) {
        return "HappyEmote";
      } else {
        return "PartyEmote";
      }
    },
    showBottomButtons() {
      // Create/Import CTA visibility depends on integration and initial product volume
      if (
        !this.isShopify &&
        !(
          (this.site.site.integration === "woocommerce" &&
            this.products.length >= 3) ||
          (this.site.site.integration === "prestashop" &&
            this.products.length >= 3)
        )
      ) {
        return true;
      } else {
        return false;
      }
    },
    comesFromStepOne() {
      // If navigated from the wizard step 1, open step 3 for selected product
      if (
        this.$route.query.campaignId &&
        this.$route.query.action !== "videoLibraryCreation"
      ) {
        return true;
      } else return false;
    },
    bulkCampaigns() {
      return this.campaigns.filter((campaign) => Array.isArray(campaign.bulk));
    },
    productsToIterate() {
      // Base list depends on context (segmentation vs full list)
      let list = this.isProductSegmentation
        ? this.segmentedProducts
        : this.productsBase;

      // Apply the "has videos" filter (if any)
      list = this.applyVideoFilter(list);
      list = this.applyStockFilter(list);
      list = this.applyStatusFilter(list);

      // For large datasets, render progressively (infinite scroll)
      return this.hasLargeAmountOfProducts
        ? list.slice(0, this.visibleProducts)
        : list;
    },
    productListCountData() {
      // UX counter "X out of Y" or just total
      if (
        typeof this.productsSearchCount === "number" &&
        this.productsSearchCount === 0
      ) {
        return 0;
      }
      if (
        !this.productsSearchCount ||
        this.productsCount === this.productsSearchCount
      )
        return this.productsCount;

      return `${this.productsSearchCount} out of ${this.productsCount}`;
    },
    isSortingEnabled() {
      // Disable sorting for huge lists unless filters shrink the result
      return (
        (!this.hasLargeAmountOfProducts || this.hasActiveFilters) &&
        this.productsSearchCount <= 500
      );
    },
    numberOfFilters() {
      // Counter badge for filters button
      const categories = this.selectedFilters.categories?.length || 0;
      const collections = this.selectedFilters.collections?.length || 0;
      const brands = this.selectedFilters.brands?.length || 0;
      const videos = this.selectedFilters.hasVideos !== null ? 1 : 0;
      const stock = this.selectedFilters.hasStock !== null ? 1 : 0;
      const status = this.selectedFilters.productStatus?.length || 0;

      return categories + collections + brands + videos + stock + status;
    },
    hasLanguageSettings() {
      const ls = this.site.site.languageSettings;
      return ls != null && Object.keys(ls).length > 0;
    },
    languageCodes() {
      // Keys of languageSettings (e.g., ['en', 'es-ES', ...])
      const ls = this.site.site.languageSettings || {};
      return Object.keys(ls);
    },
    productsBase() {
      // Preserve filtered list while step 3 modal is open
      if (this.cachedProducts && this.showStep3Modal) {
        return this.cachedProducts;
      }
      return this.products;
    },
    computeHasActiveFilters() {
      const f = this.selectedFilters;
      return (
        f.categories.length > 0 ||
        f.collections.length > 0 ||
        f.brands.length > 0 ||
        f.productName.length > 0 ||
        f.hasVideos !== null ||
        f.hasStock !== null ||
        (Array.isArray(f.productStatus) && f.productStatus.length > 0)
      );
    },
  },
  mounted() {
    // Infinite scroll for large product lists
    window.addEventListener("scroll", this.handleScroll);
  },
  async created() {
    // Initial data hydration sequence
    this.isLoading = true;

    await this.$store.dispatch("product/getProductsCount", this.siteId);

    if (!this.isProductSegmentation) {
      await this.$store.dispatch("product/getProductUniqueValues", this.siteId);

      await this.$store.dispatch("product/getProducts", {
        siteId: this.siteId,
        isProductListView: true,
      });
    }

    await this.$store.dispatch("site/getSite", {
      siteId: this.siteId,
    });

    await this.$store.dispatch("site/getIntegrations", {
      siteId: this.siteId,
    });

    await this.$store.dispatch("campaign/getCampaigns", {
      siteId: this.siteId,
    });

    // Default sort
    this.sortProducts();

    // Deep-link flows
    if (this.$route.query.action === "videoLibraryCreation") {
      this.videoLibraryCreation = true;
    } else if (this.comesFromStepOne) {
      const productId = this.$route.query.productId;
      this.addVideosToWidget(this.campaignId, productId);
    }

    this.isLoading = false;
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    // --- Basic helpers & formatting -------------------------------------------------
    calculateTotalQty(variants) {
      if (variants) {
        let totalQty = variants.reduce(
          (total, variant) => total + variant.qty,
          0
        );
        let hasUnlimited = variants.some((variant) => variant.sellWithoutStock);

        if (hasUnlimited) {
          return this.$t("create.bulk.pagesModal.stock.unlimited");
        } else if (totalQty === 0) {
          return this.$t("create.bulk.pagesModal.stock.outStock");
        } else if (!variants[0].qty) {
          return "No info";
        } else {
          return totalQty + " " + this.$t("create.bulk.pagesModal.stock.stock");
        }
      }

      return this.$t("create.bulk.pagesModal.stock.outStock");
    },
    firstVariantPrice(variants) {
      if (variants && variants.length > 0) {
        const firstVariant = variants[0];
        const price = firstVariant ? firstVariant.price : 0;

        if (this.getCurrency === "$") {
          return `$${price}`;
        } else if (this.getCurrency === "€") {
          return `${price}€`;
        } else {
          return `${price}${this.getCurrency}`;
        }
      } else {
        return "";
      }
    },
    onAutocompletedOptionSelected(productItem) {
      // Make more items visible so the target product is in DOM
      const selectedProduct = this.products.find(
        (product) => product._id === productItem._id
      );

      //Make all products visible so we can scroll
      this.visibleProducts = 500;

      if (selectedProduct) {
        this.scrollToProduct(selectedProduct);
      }
    },
    scrollToProduct(product) {
      // Smoothly scroll to card and briefly highlight it
      this.$nextTick(() => {
        const productRef = this.$refs[product._id];
        if (productRef && productRef.length > 0) {
          const firstProductRef = productRef[0];
          firstProductRef.scrollIntoView({ behavior: "smooth" });

          this.highlightProductCard(product.productId);
        }
      });
    },

    // --- Sorting logic --------------------------------------------------------------
    toggleSort(sortType) {
      if (this.hasLargeAmountOfProducts && !this.isSortingEnabled) return;
      if (this.selectedSorting === sortType) {
        this.selectedArrow = this.selectedArrow === 1 ? -1 : 1;
      } else {
        this.selectedSorting = sortType;
        this.selectedArrow = 1;
      }
      this.sortProducts();
    },
    sortProducts() {
      const productsList = [...this.products].slice().sort((a, b) => {
        let comparison = 0;

        switch (this.selectedSorting) {
          case "names":
            comparison = a.name.localeCompare(b.name) * this.selectedArrow;
            break;
          case "created":
            comparison =
              (new Date(a.creationDate).getTime() -
                new Date(b.creationDate).getTime()) *
              this.selectedArrow;
            break;
          case "status":
            let videosA = a.videosAttached ? a.videosAttached.length : 0;
            let videosB = b.videosAttached ? b.videosAttached.length : 0;
            comparison = (videosA - videosB) * this.selectedArrow;
            break;
          case "lastImport":
            if (!a.videoLastImportDate) return this.selectedArrow;
            if (!b.videoLastImportDate) return -this.selectedArrow;
            comparison =
              (new Date(a.videoLastImportDate).getTime() -
                new Date(b.videoLastImportDate).getTime()) *
              this.selectedArrow;
            break;
          case "widget":
            let widgetsA = a.formatsAttached ? a.formatsAttached.length : 0;
            let widgetsB = b.formatsAttached ? b.formatsAttached.length : 0;
            comparison = (widgetsA - widgetsB) * this.selectedArrow;
            break;
          case "quantity":
            let qtyA = this.calculateSortableQty(a.variants);
            let qtyB = this.calculateSortableQty(b.variants);
            comparison = (qtyA - qtyB) * this.selectedArrow;
            break;
          default:
            break;
        }
        return comparison;
      });
      this.$store.commit("product/setProducts", productsList);
    },
    openOptionsModal(productId) {
      this.showOptionsModal = true;
      this.selectedProductId = productId;

      this.selectedProductObject = this.products.find(
        (product) => product._id === productId
      );
    },
    closeOptionsModal() {
      this.showOptionsModal = false;
      this.selectedProductId = null;

      this.selectedProductObject = null;
    },
    openCreateProductModal() {
      this.showCreateProduct = true;
    },
    closeCreateProduct() {
      this.showCreateProduct = false;
    },
    editProduct(payload) {
      this.editProductData = this.products.find(
        (product) => product._id === payload._id
      );
      this.editMode = true;
      this.showOptionsModal = false;
      this.openCreateProductModal();
    },
    openDeleteModal(product) {
      this.productToDelete = product;
      this.showDeleteModal = true;
      this.showOptionsModal = false;
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    async deleteProduct(productId) {
      try {
        if (productId) {
          await this.$store.dispatch("product/deleteProduct", { productId });
          this.$notify({
            title: this.$t("shared.toastMessage.success"),
            text: this.$t("productPages.deleteModal.deleted"),
            type: "success",
          });
        }
      } catch (error) {
        console.error("Error deleting product:", error);
      }
    },

    // --- Formatting helpers ---------------------------------------------------------
    formattedDate(date) {
      const options = { year: "numeric", month: "short", day: "numeric" };
      return new Date(date).toLocaleDateString("en-US", options);
    },
    safeFormattedDate(date) {
      const formatted = this.formattedDate(date);
      // Check if the formatted date is valid
      if (formatted === "Invalid Date") {
        return "N/A";
      }
      return formatted;
    },
    truncateName(name) {
      return name.length > 23 ? name.substring(0, 20) + "..." : name;
    },
    getWidgetComponent(formatName) {
      switch (formatName) {
        case "carousel":
          return "CarouselWidget";
        case "story":
          return "StoriesWidget";
        case "bubble":
          return "BubbleWidget";
        case "popup":
          return "PopupWidget";
        case "embed":
          return "EmbedWidget";
        default:
          return "";
      }
    },

    // --- Add widget / Manage videos entrypoints ------------------------------------
    addWidget(
      productId,
      formatsAttached,
      videosAttached,
      productKey,
      name,
      langCode = null
    ) {
      // If no bulk campaigns exist yet, open the widget creator flow
      if (this.bulkCampaigns.length === 0) {
        this.productIdSelected = productKey || productId;
        this.productNameSelected = name;
        this.isWidgetCreatorOpened = true;
      } else {
        // Else, pre-filter available campaigns not yet attached to this product
        const attachedCampaignIds = (formatsAttached || []).map(
          (format) => format.campaignId
        );

        const filteredBulkCampaigns = this.bulkCampaigns.filter(
          (campaign) => !attachedCampaignIds.includes(campaign._id)
        );
        this.isAddWidget = true;
        this.addOrManageVideos(
          filteredBulkCampaigns,
          videosAttached,
          productId,
          productKey,
          langCode
        );
      }
    },
    getWidgetFormat(formatName) {
      switch (formatName) {
        case "carousel":
          return "Carousel";
        case "story":
          return "Stories";
        case "bubble":
          return "Bubble";
        case "popup":
          return "Popup";
        case "embed":
          return "Embed";
        default:
          return "";
      }
    },
    openEmbedCodeModal(campaignId) {
      const selectedCampaign = this.campaigns.find(
        (campaign) => campaign._id === campaignId
      );

      if (selectedCampaign) {
        this.selectedWidget = selectedCampaign;
        this.showEmbedCodeModal = true;
      }
    },
    closeEmbedCodeModal() {
      this.selectedWidget = null;
      this.showEmbedCodeModal = false;
    },

    // Decides whether we can go straight to step 3, or need to choose a widget first
    async addOrManageVideos(
      formatsAttached,
      videosAttached,
      productId,
      productKey,
      langCode = null
    ) {
      this.pendingLangCode = langCode;
      this.selectedProductId = productKey || productId;
      this.videosAttachedOnProduct = videosAttached;

      const formats = this.deduplicateFormats(formatsAttached);

      if (formats && formats.length === 1) {
        // Single target campaign -> jump directly to step 3
        this.isFetching = true;
        if (this.isAddWidget) {
          await this.addVideosToWidget(
            formats[0]._id,
            productId,
            productKey,
            langCode
          );
        } else {
          await this.addVideosToWidget(
            formats[0].campaignId,
            productId,
            productKey,
            langCode
          );
        }
      } else if (formats && formats.length > 1) {
        // Let the user pick a campaign (widget) among compatible ones
        this.openAddVideosToWidgetModal = true;
        this.possibleWidgets = formats;
      } else if (!(videosAttached && videosAttached.length)) {
        return;
      }
    },

    // --- Core: open step 3 with merged video candidates for the product/lang -------
    async addVideosToWidget(
      campaignId,
      productId,
      productKey,
      langCode = null
    ) {
      // NOTE: This method:
      // 1) finds/creates the correct bulk feed for (product, lang)
      // 2) merges videos from several segmentation sources (advanced/url/lang/root)
      // 3) opens step 3 with the deduped list and proper feed context

      langCode = langCode ?? this.pendingLangCode;
      this.selectedProductId = productKey || productId;
      this.openAddVideosToWidgetModal = false;
      this.campaignIdForStep3 = campaignId;

      if (this.videosAttachedOnProduct) {
        // Keep only videos that belong to the selected campaign (if any)
        this.videosAttachedOnProduct = this.videosAttachedOnProduct.filter(
          (v) => !v.campaignId || v.campaignId === campaignId
        );
      }

      const campaign = await this.fetchCampaign(
        this.campaignIdForStep3,
        this.siteId
      );

      // Locate an existing bulk feed for (product, lang). Fallback to product-only.
      let matchingBulk = this.findMatchingBulk(
        campaign,
        productId,
        productKey,
        langCode
      );

      this.feedWasSaved = false;

      if (matchingBulk) {
        this.$store.commit("campaign/setCurrentFeedEdit", matchingBulk);
        this.selectedCurrentFeedEdit = matchingBulk;
        this.createdFeedWasNew = false;
      } else {
        // Create a fresh feed targeting this product (+ lang if applicable)
        const newFeed = this.createNewFeed(
          campaignId,
          productId,
          productKey,
          langCode
        );
        this.$store.commit("campaign/setCurrentFeedEdit", newFeed);
        this.selectedCurrentFeedEdit = newFeed;

        this.currentFeedEditIndex = (campaign.bulk || []).length;

        // Attach the new format to the product card (local optimistic update)
        const getFormatName = (c) => {
          switch (c.formats[0].formatType) {
            case FormatType.bubble:
              return "bubble";
            case FormatType.popup:
              return "popup";
            case FormatType.embed:
              return "embed";
            case FormatType.carousel:
              return c.formats[0].isStory ? "story" : "carousel";
            default:
              return "";
          }
        };
        const product = this.getProductByProductKey(productId, productKey);
        const newFormat = {
          campaignId,
          campaignName: campaign.name,
          formatName: getFormatName(campaign),
          feedIndex: this.currentFeedEditIndex,
        };
        const updatedProduct = {
          ...product,
          _id: product._id,
          formatsAttached: product.formatsAttached
            ? [...product.formatsAttached, newFormat]
            : [newFormat],
        };
        this.$store.commit("product/updateProduct", updatedProduct);
        this.createdFeedWasNew = true;
      }

      // --- Build candidate video list from multiple segmentation sources ----------
      const videoIdOf = (v) => {
        const id = v?.videoId ?? v?._id;
        return id != null ? String(id) : "";
      };

      const mapFeedVideos = (feed) =>
        (Array.isArray(feed.videos) ? feed.videos : []).map((v) => ({
          ...v,
          isUniqueProductFeed: false,
          feedName: feed.feedName,
          campaignId: campaignId,
        }));

      const pidStrs = [productKey, productId]
        .filter((v) => v != null)
        .map(String);
      const selectedFeed = matchingBulk || null;

      // 1) Videos from other feeds that already target (product, lang) explicitly
      let videosFromOtherProductLang = [];
      if (langCode) {
        for (const feed of campaign.bulk || []) {
          if (selectedFeed && feed === selectedFeed) continue;
          const seg = feed?.segmentation || {};
          const pids = this.getSegProductIds(seg);
          const langs = this.getSegLanguages(seg);

          const hasProduct = pidStrs.some((id) => pids.has(id));
          const hasLang = langs.has(String(langCode));

          if (hasProduct && hasLang) {
            videosFromOtherProductLang.push(...mapFeedVideos(feed));
          }
        }
      }

      const mergeAndDedupe = (...arrays) => {
        const map = new Map();
        for (const arr of arrays) {
          for (const v of arr || []) {
            const vid = videoIdOf(v);
            if (!vid) continue;
            map.set(vid, v);
          }
        }
        return Array.from(map.values());
      };

      const extractAdvancedKeys = (advanced) => {
        // pull productIds + languages from advanced segmentation tree
        const productIds = new Set();
        const languages = new Set();
        const visit = (node) => {
          if (!node) return;
          if (Array.isArray(node)) return node.forEach(visit);
          if (typeof node === "object") {
            if (
              node.dataType === "products" &&
              node.comparison === "eq" &&
              Array.isArray(node.productId)
            )
              node.productId.map(String).forEach((id) => productIds.add(id));
            if (node.dataType === "language" && typeof node.string === "string")
              languages.add(node.string);
            if (Array.isArray(node.filters)) visit(node.filters);
          }
        };
        visit(advanced);
        return { productIds, languages };
      };

      // 2) Root "product only" feed (no lang) as a fallback baseline
      let rootVideos = [];
      if (langCode) {
        const rootFeed = (campaign.bulk || []).find(({ segmentation = {} }) => {
          const pIds = Array.isArray(segmentation.productId)
            ? segmentation.productId.map(String)
            : [];
          const langs = segmentation.language;
          const hasProduct = pidStrs.some((id) => pIds.includes(id));
          const hasNoLang = !Array.isArray(langs) || langs.length === 0;
          return hasProduct && hasNoLang;
        });
        if (rootFeed) rootVideos = mapFeedVideos(rootFeed);
      }

      // 3) Advanced segmentation that matches (product, lang)
      let videosFromAdvancedSegmentation = [];
      if (langCode) {
        for (const feed of campaign.bulk || []) {
          const seg = feed?.segmentation || {};
          if (!seg.advanced) continue;

          const { productIds, languages } = extractAdvancedKeys(seg.advanced);

          const requiresProduct = productIds.size > 0;
          const requiresLang = languages.size > 0;

          const okProduct =
            !requiresProduct || pidStrs.some((id) => productIds.has(id));
          const okLang = !requiresLang || languages.has(langCode);

          if (okProduct && okLang) {
            videosFromAdvancedSegmentation.push(...mapFeedVideos(feed));
          }
        }
      }

      // 4) URL-based segmentation (direct url array) matching this product URL + lang
      let videosFromUrlSegmentation = [];
      if (langCode) {
        const productObj = this.getProductByProductKey(productId, productKey);
        const productUrl = productObj?.productUrl || "";
        if (productUrl) {
          for (const feed of campaign.bulk || []) {
            const seg = feed?.segmentation || {};
            const langs = Array.isArray(seg.language) ? seg.language : [];
            const hasLang = langs.includes(langCode);
            const hasProductIds =
              Array.isArray(seg.productId) && seg.productId.length > 0;
            const hasUrlSeg = Array.isArray(seg.url) && seg.url.length > 0;

            if (hasLang && !hasProductIds && hasUrlSeg) {
              const matchesUrl = this.matchAnyUrlSeg(seg.url, productUrl);
              if (matchesUrl) {
                videosFromUrlSegmentation.push(...mapFeedVideos(feed));
              }
            }
          }
        }
      }

      // 5) URL conditions nested inside advanced tree
      let videosFromUrlAdvanced = [];
      if (langCode) {
        const productObj = this.getProductByProductKey(productId, productKey);
        const productUrl = productObj?.productUrl || "";
        if (productUrl) {
          for (const feed of campaign.bulk || []) {
            const seg = feed?.segmentation || {};
            if (!seg.advanced) continue;

            const { productIds, languages } = extractAdvancedKeys(seg.advanced);
            const urlTree = this.extractAdvancedUrlSubtree(seg.advanced);

            const requiresProduct = productIds.size > 0;
            const requiresLang = languages.size > 0;
            const requiresUrl = !!urlTree;

            const okProduct =
              !requiresProduct || pidStrs.some((id) => productIds.has(id));
            const okLang = !requiresLang || languages.has(langCode);
            const okUrl =
              !requiresUrl || this.evaluateUrlNode(urlTree, productUrl);

            if (okProduct && okLang && okUrl) {
              videosFromUrlAdvanced.push(...mapFeedVideos(feed));
            }
          }
        }
      }

      // 6) Lang-only feeds (no productId, no url) that include this lang
      let videosFromLangOnly = [];
      if (langCode) {
        for (const feed of campaign.bulk || []) {
          const seg = feed?.segmentation || {};
          const langs = Array.isArray(seg.language) ? seg.language : [];
          const hasLang = langs.includes(langCode);
          const hasNoProducts =
            !Array.isArray(seg.productId) || seg.productId.length === 0;
          const hasNoUrl = !Array.isArray(seg.url) || seg.url.length === 0;

          if (hasLang && hasNoProducts && hasNoUrl && !seg.advanced) {
            videosFromLangOnly.push(...mapFeedVideos(feed));
          }

          if (seg.advanced) {
            const { productIds, languages } = extractAdvancedKeys(seg.advanced);
            const urlTree = this.extractAdvancedUrlSubtree(seg.advanced);
            const isLangOnlyAdv =
              productIds.size === 0 && !urlTree && languages.has(langCode);
            if (isLangOnlyAdv) {
              videosFromLangOnly.push(...mapFeedVideos(feed));
            }
          }
        }
      }

      // Merge all the candidates with current attached (campaign-scoped), dedupe by video id
      this.videosAttachedOnProduct = mergeAndDedupe(
        this.videosAttachedOnProduct || [],
        videosFromAdvancedSegmentation,
        videosFromUrlSegmentation,
        videosFromUrlAdvanced,
        videosFromLangOnly,
        rootVideos,
        videosFromOtherProductLang
      );

      // Open Step 3
      this.showStep3Modal = true;
    },

    updateProductVideosAttached(productId, videos) {
      // Only update "root" (non-lang) attachments if no language context is active
      if (this.pendingLangCode) return;
      const product = this.getProductByProductKey(productId);
      const addedVideos = videos.map((newVideo) => ({
        _id: newVideo._id,
        fileName: newVideo.fileName,
        videoId: newVideo._id,
      }));
      const updatedProduct = {
        ...product,
        _id: product._id,
        videosAttached: addedVideos,
      };
      this.$store.commit("product/updateProduct", updatedProduct);
    },
    getProductByProductKey(productId, productKey) {
      // Accept either productKey or numeric/string productId
      const product = this.products.find((product) =>
        productKey != null
          ? product.productId === productKey
          : product.productId === String(productId) ||
            product.productId === parseInt(productId)
      );
      return product || null;
    },

    // Close Step 3, clean up temp feed if not saved, refresh campaign in store
    async closeStep3Modal() {
      // Delete feed if we didn't save
      if (
        this.createdFeedWasNew &&
        !this.feedWasSaved &&
        this.campaignIdForStep3 != null &&
        typeof this.currentFeedEditIndex === "number"
      ) {
        try {
          await this.$store.dispatch("campaign/deleteFeed", {
            campaignId: this.campaignIdForStep3,
            feedIndex: this.currentFeedEditIndex,
          });

          // Remove optimistic format from product
          const product = this.getProductByProductKey(this.selectedProductId);
          if (product && Array.isArray(product.formatsAttached)) {
            const updatedFormats = product.formatsAttached.filter(
              (f) =>
                !(
                  String(f.campaignId) === String(this.campaignIdForStep3) &&
                  Number(f.feedIndex) === Number(this.currentFeedEditIndex)
                )
            );
            this.$store.commit("product/updateProduct", {
              ...product,
              _id: product._id,
              formatsAttached: updatedFormats,
            });
          }
        } catch (err) {
          console.error("Error deleting just-created feed:", err);
        }
      }

      if (this.campaignIdForStep3) {
        await this.syncCampaignInStore(this.campaignIdForStep3);
      }

      // Remove duplicates between root and lang-scoped attachments
      this.normalizeProductAttachmentsAfterEdit(this.selectedProductId);
      this.showStep3Modal = false;
      this.isFetching = false;
      this.campaignIdForStep3 = null;
      this.isAddWidget = false;
      this.$store.commit("campaign/setCurrentFeedEdit", null);
      this.lastEditedProduct = this.selectedProductId;
      this.highlightProductCard(this.lastEditedProduct);
      this.pendingLangCode = null;
      this.feedWasSaved = false;
      this.createdFeedWasNew = false;
      this.cachedProducts = null;
    },
    closeAddVideosToWidgetModal() {
      this.openAddVideosToWidgetModal = false;
      this.isAddWidget = false;
    },

    // --- Store utilities ------------------------------------------------------------
    async fetchCampaign(campaignId, siteId) {
      const { campaign } = await this.$store.dispatch("campaign/getSingle", {
        campaignId: campaignId,
        siteId: siteId,
      });
      return campaign;
    },
    findMatchingBulk(campaign, productId, productKey, langCode = null) {
      // Try to find a feed that matches product + lang; else product-only
      const feeds = campaign.bulk || [];
      const pidStrs = [productKey, productId].filter(Boolean).map(String);

      const hasProduct = (seg) => {
        const pids = this.getSegProductIds(seg);
        return pidStrs.some((id) => pids.has(id));
      };
      const hasLang = (seg) => {
        if (!langCode) return false;
        const langs = this.getSegLanguages(seg);
        return langs.has(String(langCode));
      };

      if (langCode) {
        const f = feeds.find(
          ({ segmentation = {} }) =>
            hasProduct(segmentation) && hasLang(segmentation)
        );
        if (f) {
          this.currentFeedEditIndex = feeds.indexOf(f);
          return f;
        }
      }

      const f2 = feeds.find(({ segmentation = {} }) => {
        const langs = this.getSegLanguages(segmentation);
        return hasProduct(segmentation) && langs.size === 0;
      });
      if (f2) {
        this.currentFeedEditIndex = feeds.indexOf(f2);
        return f2;
      }

      return null;
    },
    createNewFeed(campaignId, productId, productKey, langCode = null) {
      let matchingProduct = this.products.find(
        (product) =>
          product.productId === String(productId) ||
          product.productId === parseInt(productId)
      );
      const newSegmentation = { productId: [productKey || productId] };
      if (langCode) {
        newSegmentation.language = [langCode];
      }
      const feedName = langCode
        ? `${langCode.toUpperCase()} - ${matchingProduct.name}`
        : matchingProduct.name;

      const newFeed = {
        feedName,
        segmentation: newSegmentation,
        videos: [],
        creationDate: new Date().toISOString(),
      };

      this.$store.dispatch("campaign/createFeed", { campaignId, newFeed });
      return newFeed;
    },
    async forceSync() {
      // Pull fresh products from integration backend
      this.isSyncing = true;
      try {
        await this.$store.dispatch("product/forceSyncProducts", this.siteId);
        this.$notify({
          title: this.$t("shared.toastMessage.success"),
          text: this.$t("productPages.updatedList"),
          type: "success",
        });
      } catch (error) {
        console.error("Error syncing products:", error);
        this.$notify({
          title: this.$t("shared.toastMessage.error"),
          text: error,
          type: "error",
        });
      }
      this.isSyncing = false;
    },
    calculateSortableQty(variants) {
      // Used only for sorting; Infinity means "unlimited stock" sorts last/first depending on arrow
      if (!variants || variants.length === 0) {
        return 0;
      }

      let hasUnlimited = variants.some((variant) => variant.sellWithoutStock);

      if (hasUnlimited) {
        return Infinity;
      }

      return variants.reduce((total, variant) => total + (variant.qty || 0), 0);
    },
    async openWidgetFormat(campaignId) {
      // Open feed list modal for a given campaign
      this.campaignIdForStep3 = campaignId;
      let campaign = await this.fetchCampaign(
        this.campaignIdForStep3,
        this.siteId
      );
      this.$store.commit("campaign/setFeedList", campaign.bulk);
      this.showWidgetFormatModal = true;
    },
    closeWidgetFormalModal() {
      this.showWidgetFormatModal = false;
    },
    closeWidgetCreator() {
      this.isWidgetCreatorOpened = false;
    },
    removeProductSegmentation(productId) {
      this.$emit("remove-product-segmentation", productId);
    },

    // --- UI niceties ---------------------------------------------------------------
    handleMouseEnter(productId) {
      this.hoveredProductId = productId;
    },
    handleMouseLeave() {
      this.hoveredProductId = null;
    },
    highlightProductCard(productId) {
      // Briefly tint product card for visual feedback
      if (productId) {
        const product = this.products.find(
          (p) =>
            p.productId === String(productId) ||
            p.productId === parseInt(productId)
        );
        if (product) {
          const productRefId = product._id;

          const productRef = this.$refs[productRefId];
          if (productRef && productRef.length > 0) {
            const productCard = productRef[0].querySelector(".product-card");
            if (productCard) {
              productCard.style.backgroundColor = "rgba(226, 124, 252, 0.2)";

              setTimeout(() => {
                productCard.style.backgroundColor = "";
              }, 5000);
            }
          }
        }
      }
    },

    // --- Filtering / search for large catalogs ------------------------------------
    openFilterModal() {
      this.showFilterModal = true;
    },
    async searchOnLargeAmountOfProducts(payload) {
      // Server-driven search (brand/category/collection/name) for big datasets
      if (payload) {
        this.loadingLargeAmountProducts = true;
      }
      this.selectedFilters = {
        ...this.selectedFilters,
        ...payload,
      };
      this.hasActiveFilters = this.computeHasActiveFilters;

      const query = {
        brand: this.selectedFilters.brands,
        category: this.selectedFilters.categories,
        collections: this.selectedFilters.collections,
        name: this.selectedFilters.productName,
        ...(this.selectedFilters.hasVideos !== null && {
          hasVideos: this.selectedFilters.hasVideos,
        }),
        ...(this.selectedFilters.hasStock !== null && {
          hasStock: this.selectedFilters.hasStock,
        }),

        ...(this.isShopify &&
          Array.isArray(this.selectedFilters.productStatus) &&
          this.selectedFilters.productStatus.length > 0 && {
            productStatus: this.selectedFilters.productStatus,
          }),
      };

      Object.keys(query).forEach((key) => {
        if (Array.isArray(query[key]) && query[key].length === 0)
          delete query[key];
        if (query[key] === "") delete query[key];
      });

      if (this.isAborted) {
        this.loadingLargeAmountProducts = false;
        return;
      }

      try {
        await this.$store.dispatch("product/getProducts", {
          siteId: this.siteId,
          isProductListView: true,
          query,
        });

        // Save snapshot so step 3 doesn't wipe the filtered list
        this.cachedProducts = [...this.products];

        if (!this.isAborted) {
          this.loadingLargeAmountProducts = false;
          this.selectedSorting = "created";
          this.selectedArrow = -1;
        }
      } catch (error) {
        console.error(error);
        this.loadingLargeAmountProducts = false;
      }
    },

    // --- Infinite scroll -----------------------------------------------------------
    handleScroll() {
      const scrollBottom = window.innerHeight + window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;

      if (scrollBottom >= documentHeight - 10 && !this.isLoadingMore) {
        this.loadMoreProducts();
      }
    },
    loadMoreProducts() {
      if (this.visibleProducts <= this.productsToIterate.length) {
        this.isLoadingMore = true;

        setTimeout(() => {
          this.visibleProducts += 10;
          this.isLoadingMore = false;
        }, 1000);
      }
    },

    // --- Reset / stop search -------------------------------------------------------
    resetFilters() {
      this.selectedFilters = {
        categories: [],
        collections: [],
        brands: [],
        productName: "",
        hasVideos: null,
        hasStock: null,
        productStatus: [],
      };
      this.cachedProducts = null;
      this.searchOnLargeAmountOfProducts({});
    },
    stopSearch() {
      // Abort flag is honored by searchOnLargeAmountOfProducts
      this.isAborted = true;
      this.loadingLargeAmountProducts = false;
    },

    // --- Local filters (has videos) -----------------------------------------------
    applyVideoFilter(products) {
      if (this.selectedFilters.hasVideos === null) return products;

      if (this.hasLargeAmountOfProducts) return products;

      return products.filter((product) => {
        const hasAny =
          Array.isArray(product.videosAttached) &&
          product.videosAttached.length > 0;
        return this.selectedFilters.hasVideos ? hasAny : !hasAny;
      });
    },
    filterProductsWithVideos({ hasVideos }) {
      this.selectedFilters.hasVideos = hasVideos;
      this.hasActiveFilters = this.computeHasActiveFilters;
      if (this.hasLargeAmountOfProducts) this.searchOnLargeAmountOfProducts({});
    },

    // --- Localization row expand/collapse -----------------------------------------
    toggleLanguageExpansion(productId) {
      this.$set(
        this.expandedProducts,
        productId,
        !this.expandedProducts[productId]
      );
    },
    getLocaleFlag(langCode) {
      const loc = this.locales.find(
        (l) => l.code.replace(/\//g, "") === langCode
      );
      return loc ? loc.flag : "";
    },

    // --- Utilities for formats and feeds ------------------------------------------
    uniqueFormats(formats) {
      return this.deduplicateFormats(formats);
    },
    getAllFeeds() {
      return this.bulkCampaigns.flatMap((c) => c.bulk || []);
    },

    // Count distinct video IDs reachable for (product, lang) across all feed types
    countVideosForLang(productId, productKey, langCode) {
      const product = this.getProductByProductKey(productId, productKey);
      const rootVideos = Array.isArray(product?.videosAttached)
        ? product.videosAttached
        : [];

      const videoIdOf = (v) => {
        const id = v?.videoId ?? v?._id ?? v?.id;
        return id != null ? String(id) : "";
      };

      const rootIds = new Set(rootVideos.map(videoIdOf).filter(Boolean));

      const pidStrs = [productKey, productId].filter(Boolean).map(String);
      const productUrl = product?.productUrl || "";
      const feeds = this.getAllFeeds();

      const feedIds = new Set();

      const addFeedVideos = (feed) => {
        const vids = Array.isArray(feed.videos) ? feed.videos : [];
        for (const v of vids) {
          const id = videoIdOf(v);
          if (id) feedIds.add(id);
        }
      };

      for (const feed of feeds) {
        const seg = feed?.segmentation || {};

        const directProductIds = new Set(
          (Array.isArray(seg.productId) ? seg.productId : []).map(String)
        );
        const directLangs = new Set(
          Array.isArray(seg.language) ? seg.language : []
        );

        const hasProductDirect = pidStrs.some((id) => directProductIds.has(id));
        const hasLangDirect = directLangs.has(langCode);

        if (hasProductDirect && hasLangDirect) {
          addFeedVideos(feed);
        }

        const { productIds: advPids, languages: advLangs } =
          this.extractAdvancedKeysStrict(seg.advanced);

        const hasProductAdv = pidStrs.some((id) => advPids.has(id));
        const hasLangAdv = advLangs.has(langCode);

        if (hasProductAdv && hasLangAdv) {
          addFeedVideos(feed);
        }

        if (
          hasLangDirect &&
          directProductIds.size === 0 &&
          (!Array.isArray(seg.url) || seg.url.length === 0) &&
          !seg.advanced
        ) {
          addFeedVideos(feed);
        }

        if (productUrl) {
          const hasProductIds = directProductIds.size > 0;
          const hasUrlSeg = Array.isArray(seg.url) && seg.url.length > 0;
          if (hasLangDirect && !hasProductIds && hasUrlSeg) {
            const matchesUrl = this.matchAnyUrlSeg(seg.url, productUrl);
            if (matchesUrl) addFeedVideos(feed);
          }
        }

        if (seg.advanced) {
          const { productIds: aPids, languages: aLangs } =
            this.extractAdvancedKeysStrict(seg.advanced);
          const urlTree = this.extractAdvancedUrlSubtree(seg.advanced);

          const isLangOnlyAdv =
            aPids.size === 0 && !urlTree && aLangs.has(langCode);
          if (isLangOnlyAdv) {
            addFeedVideos(feed);
          }
        }

        if (productUrl && seg.advanced) {
          const { productIds: aPids, languages: aLangs } =
            this.extractAdvancedKeysStrict(seg.advanced);
          const urlTree = this.extractAdvancedUrlSubtree(seg.advanced);

          const requiresProduct = aPids.size > 0;
          const requiresLang = aLangs.size > 0;
          const requiresUrl = !!urlTree;

          const okProduct =
            !requiresProduct || pidStrs.some((id) => aPids.has(id));
          const okLang = !requiresLang || aLangs.has(langCode);
          const okUrl =
            !requiresUrl || this.evaluateUrlNode(urlTree, productUrl);

          if (
            okProduct &&
            okLang &&
            okUrl &&
            (requiresProduct || requiresLang || requiresUrl)
          ) {
            addFeedVideos(feed);
          }
        }
      }

      return new Set([...rootIds, ...feedIds]).size;
    },

    // Parse "advanced" object to get productIds/languages strictly (no URL tree)
    extractAdvancedKeysStrict(advanced) {
      const productIds = new Set();
      const languages = new Set();

      const visit = (node) => {
        if (!node) return;
        if (Array.isArray(node)) {
          node.forEach(visit);
          return;
        }
        if (typeof node === "object") {
          if (
            node.dataType === "products" &&
            node.comparison === "eq" &&
            Array.isArray(node.productId)
          ) {
            node.productId.map(String).forEach((id) => productIds.add(id));
          }
          if (node.dataType === "language" && typeof node.string === "string") {
            languages.add(node.string);
          }
          if (Array.isArray(node.filters)) visit(node.filters);
        }
      };

      visit(advanced);
      return { productIds, languages };
    },
    idToStr(id) {
      return id != null ? String(id) : "";
    },
    deduplicateFormats(arr) {
      // Ensure each campaign appears once in UI (avoid duplicates from mixes)
      const seen = new Set();
      return (arr || []).filter((f) => {
        const id = this.idToStr(f?.campaignId ?? f?._id);
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
    },
    async syncCampaignInStore(campaignId) {
      const { campaign } = await this.$store.dispatch("campaign/getSingle", {
        siteId: this.siteId,
        campaignId,
      });
      this.$store.commit("campaign/updateCampaign", { campaign });
    },

    // --- Product properties modal --------------------------------------------------
    openProductPropertiesModal(product) {
      this.productForPropertiesModal = product;
      this.showProductPropertiesModal = true;
    },

    // Remove from product root attachments any video that is already covered by lang feeds
    normalizeProductAttachmentsAfterEdit(productIdOrKey) {
      const product = this.getProductByProductKey(productIdOrKey);
      if (!product) return;

      const attached = Array.isArray(product.videosAttached)
        ? product.videosAttached
        : [];

      const pidStrs = [product.productId, product.productKey, productIdOrKey]
        .filter(Boolean)
        .map((v) => String(v));

      const feeds = this.getAllFeeds();

      const langFeedVideoIds = new Set();

      const videoIdOf = (v) => {
        const id = v?.videoId ?? v?._id ?? v?.id;
        return id != null ? String(id) : "";
      };

      for (const feed of feeds) {
        const seg = feed?.segmentation || {};

        const directP = new Set(
          (Array.isArray(seg.productId) ? seg.productId : []).map(String)
        );
        const directLangs = new Set(
          Array.isArray(seg.language) ? seg.language : []
        );

        const hasProductDirect = pidStrs.some((id) => directP.has(id));
        const hasLangDirect = directLangs.size > 0;

        const { productIds: advP, languages: advL } =
          this.extractAdvancedKeysStrict(seg.advanced);

        const hasProductAdv = pidStrs.some((id) => advP.has(id));
        const hasLangAdv = advL.size > 0;

        const isLangFeed =
          (hasProductDirect && hasLangDirect) || (hasProductAdv && hasLangAdv);

        if (!isLangFeed) continue;

        const vids = Array.isArray(feed.videos) ? feed.videos : [];
        for (const v of vids) {
          const id = videoIdOf(v);
          if (id) langFeedVideoIds.add(id);
        }
      }

      const cleaned = [];
      const seen = new Set();
      for (const v of attached) {
        const id = videoIdOf(v);
        if (!id || seen.has(id)) continue;
        if (!langFeedVideoIds.has(id)) {
          cleaned.push(v);
          seen.add(id);
        }
      }

      const changed =
        cleaned.length !== attached.length ||
        cleaned.some(
          (v, i) =>
            (v?.videoId ?? v?._id) !==
            (attached[i]?.videoId ?? attached[i]?._id)
        );

      if (changed) {
        this.$store.commit("product/updateProduct", {
          ...product,
          _id: product._id,
          videosAttached: cleaned,
        });
      }
    },

    // --- URL segmentation evaluator -----------------------------------------------
    evaluateUrlLeaf(node, productUrl) {
      const comp = String(node?.comparison || "").toLowerCase();
      const rule = String(node?.string || "").trim();
      const url = String(productUrl || "").trim();

      if (!comp || !rule) return false;

      const urlLC = url.toLowerCase();
      const ruleLC = rule.toLowerCase();

      switch (comp) {
        case "contains":
          return urlLC.includes(ruleLC);
        case "not_contains":
          return !urlLC.includes(ruleLC);
        case "eq":
          return urlLC === ruleLC;
        case "not_eq":
          return urlLC !== ruleLC;
        default:
          return false;
      }
    },

    evaluateUrlNode(node, productUrl) {
      // Recursively evaluate AND/OR tree with leaf comparisons
      if (!node) return false;

      const hasChildren =
        Array.isArray(node.filters) && node.filters.length > 0;
      if (!hasChildren) {
        return this.evaluateUrlLeaf(node, productUrl);
      }

      const type = String(node.type || "and").toLowerCase(); // and/or
      const results = node.filters.map((child) =>
        this.evaluateUrlNode(child, productUrl)
      );
      return type === "or" ? results.some(Boolean) : results.every(Boolean);
    },

    matchAnyUrlSeg(urlSegArray, productUrl) {
      // Accepts array of groups (each group is an URL tree)
      if (!Array.isArray(urlSegArray) || !urlSegArray.length) return false;
      return urlSegArray.some((group) =>
        this.evaluateUrlNode(group, productUrl)
      );
    },
    extractAdvancedUrlSubtree(advanced) {
      // Extract only "url" sub-conditions from advanced tree; normalize into { type, filters[] }
      const build = (node) => {
        if (!node) return null;

        if (Array.isArray(node)) {
          const children = node.map(build).filter(Boolean);
          if (!children.length) return null;
          return { type: "and", filters: children };
        }

        if (typeof node === "object") {
          const hasChildren =
            Array.isArray(node.filters) && node.filters.length > 0;

          if (!hasChildren) {
            return node.dataType === "url"
              ? { comparison: node.comparison, string: node.string }
              : null;
          }

          const children = node.filters.map(build).filter(Boolean);
          if (!children.length) return null;
          return {
            type: String(node.type || "and").toLowerCase(),
            filters: children,
          };
        }

        return null;
      };

      return build(advanced);
    },

    // --- Campaign helpers ----------------------------------------------------------
    getFormatNameFromCampaign(campaign) {
      const f = campaign?.formats?.[0];
      if (!f) return "";
      switch (f.formatType) {
        case FormatType.bubble:
          return "bubble";
        case FormatType.popup:
          return "popup";
        case FormatType.embed:
          return "embed";
        case FormatType.carousel:
          return f.isStory ? "story" : "carousel";
        default:
          return "";
      }
    },
    findLangFormats(productId, productKey, langCode) {
      // Discover campaigns whose feeds *effectively* target (product/lang) by any segmentation mode
      const product = this.getProductByProductKey(productId, productKey);
      const productUrl = product?.productUrl || "";
      const pidStrs = [productKey, productId].filter(Boolean).map(String);

      const matches = [];

      for (const campaign of this.bulkCampaigns) {
        const feeds = campaign.bulk || [];
        const formatName = this.getFormatNameFromCampaign(campaign);

        let added = false;

        for (let i = 0; i < feeds.length && !added; i++) {
          const feed = feeds[i];
          const seg = feed?.segmentation || {};

          const directProductIds = new Set(
            (Array.isArray(seg.productId) ? seg.productId : []).map(String)
          );
          const directLangs = new Set(
            Array.isArray(seg.language) ? seg.language : []
          );

          const hasProductDirect = pidStrs.some((id) =>
            directProductIds.has(id)
          );
          const hasLangDirect = directLangs.has(langCode);

          if (hasProductDirect && hasLangDirect) {
            matches.push({
              campaignId: campaign._id,
              campaignName: campaign.name,
              formatName,
              feedIndex: i,
            });
            added = true;
            break;
          }

          const { productIds: advPids, languages: advLangs } =
            this.extractAdvancedKeysStrict(seg.advanced);
          const hasProductAdv = pidStrs.some((id) => advPids.has(id));
          const hasLangAdv = advLangs.has(langCode);

          if (hasProductAdv && hasLangAdv) {
            matches.push({
              campaignId: campaign._id,
              campaignName: campaign.name,
              formatName,
              feedIndex: i,
            });
            added = true;
            break;
          }

          if (
            hasLangDirect &&
            directProductIds.size === 0 &&
            (!Array.isArray(seg.url) || seg.url.length === 0) &&
            !seg.advanced
          ) {
            matches.push({
              campaignId: campaign._id,
              campaignName: campaign.name,
              formatName,
              feedIndex: i,
            });
            added = true;
            break;
          }

          if (productUrl) {
            const hasProductIds = directProductIds.size > 0;
            const hasUrlSeg = Array.isArray(seg.url) && seg.url.length > 0;
            if (hasLangDirect && !hasProductIds && hasUrlSeg) {
              const matchesUrl = this.matchAnyUrlSeg(seg.url, productUrl);
              if (matchesUrl) {
                matches.push({
                  campaignId: campaign._id,
                  campaignName: campaign.name,
                  formatName,
                  feedIndex: i,
                });
                added = true;
                break;
              }
            }
          }

          if (seg.advanced) {
            const { productIds: aPids, languages: aLangs } =
              this.extractAdvancedKeysStrict(seg.advanced);
            const urlTree = this.extractAdvancedUrlSubtree(seg.advanced);
            const isLangOnlyAdv =
              aPids.size === 0 && !urlTree && aLangs.has(langCode);
            if (isLangOnlyAdv) {
              matches.push({
                campaignId: campaign._id,
                campaignName: campaign.name,
                formatName,
                feedIndex: i,
              });
              added = true;
              break;
            }
          }

          if (productUrl && seg.advanced) {
            const { productIds: aPids, languages: aLangs } =
              this.extractAdvancedKeysStrict(seg.advanced);
            const urlTree = this.extractAdvancedUrlSubtree(seg.advanced);

            const requiresProduct = aPids.size > 0;
            const requiresLang = aLangs.size > 0;
            const requiresUrl = !!urlTree;

            const okProduct =
              !requiresProduct || pidStrs.some((id) => aPids.has(id));
            const okLang = !requiresLang || aLangs.has(langCode);
            const okUrl =
              !requiresUrl || this.evaluateUrlNode(urlTree, productUrl);

            if (
              okProduct &&
              okLang &&
              okUrl &&
              (requiresProduct || requiresLang || requiresUrl)
            ) {
              matches.push({
                campaignId: campaign._id,
                campaignName: campaign.name,
                formatName,
                feedIndex: i,
              });
              added = true;
              break;
            }
          }
        }
      }

      return matches;
    },
    getFormatsForLang(formatsAttached, productId, productKey, langCode) {
      // Combine attached formats with implicit ones discovered for (product/lang)
      if (!langCode)
        return Array.isArray(formatsAttached) ? formatsAttached : [];

      const count = this.countVideosForLang(productId, productKey, langCode);
      const base = Array.isArray(formatsAttached) ? formatsAttached : [];
      if (count === 0) return base;

      const found = this.findLangFormats(productId, productKey, langCode);

      return [...base, ...found];
    },
    formatsForLangUnique(formatsAttached, productId, productKey, langCode) {
      // Same as getFormatsForLang but deduped
      const base = Array.isArray(formatsAttached) ? formatsAttached : [];

      if (!langCode) return this.uniqueFormats(base);

      const found = this.findLangFormats(productId, productKey, langCode) || [];

      return this.uniqueFormats([...base, ...found]);
    },

    hasRoomForMoreWidgets(formatsAttached, productId, productKey, langCode) {
      // Whether product can attach any more bulk campaign formats for this lang
      const current = this.formatsForLangUnique(
        formatsAttached,
        productId,
        productKey,
        langCode
      );
      const attachedIds = new Set(
        current.map((f) => this.idToStr(f.campaignId))
      );
      const remaining = this.bulkCampaigns.filter(
        (c) => !attachedIds.has(this.idToStr(c._id))
      );
      return remaining.length > 0;
    },

    // --- Segmentation helpers ------------------------------------------------------
    getSegProductIds(seg) {
      const list = Array.isArray(seg?.productId) ? seg.productId : [];
      return new Set(list.map((v) => String(v)));
    },
    getSegLanguages(seg) {
      const lang = seg?.language;
      if (lang == null) return new Set();
      if (Array.isArray(lang)) return new Set(lang.map(String));
      return new Set([String(lang)]);
    },

    onFeedSaved() {
      this.feedWasSaved = true;
    },
    hasStock(variants) {
      const qty = this.calculateSortableQty(variants);
      return qty === Infinity || qty > 0;
    },
    applyStockFilter(products) {
      if (this.selectedFilters.hasStock === null) return products;

      if (this.hasLargeAmountOfProducts) return products;

      return products.filter((p) => {
        const inStock = this.hasStock(p.variants);
        return this.selectedFilters.hasStock ? inStock : !inStock;
      });
    },
    filterProductsWithStock({ hasStock }) {
      this.selectedFilters.hasStock = hasStock;
      this.hasActiveFilters = this.computeHasActiveFilters;
      if (this.hasLargeAmountOfProducts) this.searchOnLargeAmountOfProducts({});
    },
    applyStatusFilter(products) {
      if (this.hasLargeAmountOfProducts) return products;

      const arr = this.selectedFilters.productStatus || [];
      if (!Array.isArray(arr) || arr.length === 0) return products;
      const wanted = new Set(arr.map((s) => String(s).toLowerCase()));
      return products.filter((p) =>
        wanted.has(String(p.status || "").toLowerCase())
      );
    },
    filterProductsWithStatus({ status }) {
      this.selectedFilters.productStatus = Array.isArray(status) ? status : [];
      this.hasActiveFilters = this.computeHasActiveFilters;
      if (this.hasLargeAmountOfProducts) this.searchOnLargeAmountOfProducts({});
    },
  },
};
</script>


<style lang="scss">
.widget-info-tippy .tippy-tooltip {
  max-width: 400px !important;
}
</style>

<style lang="scss" scoped>
$square1-color: #f3c2ff;
$square2-color: #e27cfc;
$square3-color: #af3ed8;
$square4-color: #7b01b5;
.product-pages-wrapper {
  width: 100%;
  height: 100%;
}

.fixed-product-pages {
  position: fixed;
  z-index: 350;
}
.products-wrapper {
  margin-right: 30px;
  margin-left: 100px;
}

.product-segmentation-main-content-wrapper {
  margin-right: 0;
  margin-left: 0;
  padding-top: 0;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.bottom-buttons {
  display: flex;
  margin-bottom: 36px;
}

.main-button {
  @include base-font;
  @include font-smaller;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 24px;
  background: white;
  border: 1px solid $medium-grey;
}

.first-button {
  border-radius: 3px 0px 0px 3px;
}

.second-button {
  border-radius: 0px 3px 3px 0px;
  border-left: none;
}

.sub-header {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  position: relative;
}

.search-filter-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  width: 200%;
}

.sort-button {
  display: flex;
  gap: 8px;
  align-items: center;
  border: 1px solid $dark-grey;
  border-radius: 2px;
  padding: 4px 12px;
  height: 14px;
  &:hover {
    background: #f1f1f6;
    cursor: pointer;
    svg {
      path {
        fill: $dark-grey;
      }
    }
  }
}

.product-cards-container {
  margin-bottom: 16px;
}

.larger-margin {
  margin-bottom: 24px;
}

.product-list-header {
  display: grid;
  grid-template-columns: 2.5fr 1fr 1fr 1fr 1fr 1.8fr 0.5fr;
  color: #585858;
  background: #ececec;
  border: 1px solid #ececec;
  box-sizing: border-box;
  border-radius: 2px;
  align-items: center;
  text-align: start;
  @include font-small;
  height: 100%;
  padding: 14px;
  position: sticky;
  top: 0;
  z-index: 20;
}

.product-list-header-localization {
  grid-template-columns: 2.5fr 1fr 1fr 1fr 1fr 1fr 1.8fr 0.5fr;
}

.product-list-header-segmented-products {
  grid-template-columns: 2fr 1fr 1fr 1fr 2fr 0.25fr;
}

.product-cards {
  position: relative;
}

.product-card {
  display: grid;
  grid-template-columns: 2.5fr 1fr 1fr 1fr 1fr 1.8fr 0.5fr;
  padding: 8px 14px;
  align-items: center;
  border-bottom: 1px solid $light-grey;
  &:hover {
    background-color: #c4c4c420;
  }
}
.product-card-localization {
  grid-template-columns: 2.5fr 1fr 1fr 1fr 1fr 1fr 1.8fr 0.5fr;
}

.product-card-product-segmentation {
  grid-template-columns: 2fr 1fr 1fr 1fr 2fr 0.25fr;
}

.image-name {
  display: flex;
  align-items: center;
  gap: 16px;
  // overflow: hidden;
  text-overflow: ellipsis;
}

.product-img-wrapper {
  width: 46px;
  height: 48px;
  position: relative;
}
.product-img {
  width: 46px;
  height: 46px;
  border-radius: 2px;
  border: 1px solid $light-grey;
}

.product-link {
  display: none;
  position: absolute;
  width: 47px;
  height: 47px;
  top: 0;
  left: 0;
  background-color: rgba(6, 0, 7, 0.452);
  justify-content: center;
  align-items: center;
}
.product-link.visible {
  display: flex;
}
.new-tab-icon {
  path {
    fill: white;
  }
}

.name-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.product-name {
  @include font-small;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-info-flex {
  display: flex;
}

.product-info {
  @include font-smaller;
  font-weight: 400;
  color: #7f7f7f;
}

.option-button {
  background: none;
  padding: 0;
  width: 54px;
  align-self: flex-end;
  display: flex;
  align-items: center;
  justify-self: flex-end;
  img {
    margin-top: 2px;
  }
}

.no-products {
  height: 300px;
  background-color: $light-pink;
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 40px;
  margin-top: 16px;
}

.no-products-image {
  display: flex;
  justify-content: center;
}

.no-products-text {
  display: flex;
  flex-direction: column;
  gap: 40px;
  p {
    @include base-font;
    font-size: 24px;
    font-weight: 400;
    line-height: 30px;
  }
}
.second-sentence {
  p {
    display: inline;
  }
  .bold {
    font-weight: 700;
  }
}

.no-products-instructions {
  width: calc(20% - 20px);
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: flex-start;
  .instructions-text {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 108px;
    height: 116px;
    border: 2px solid $dark-grey;
    border-radius: 4px;
    padding: 16px 16px;
    box-sizing: border-box;
    p {
      @include font-normal;
      text-align: center;
      font-family: "Gloria Hallelujah";
    }
  }
}

.sort-button {
  position: relative;
  display: flex;
  gap: 8px;
  align-items: center;
  border: 1px solid $dark-grey;
  border-radius: 2px;
  padding: 4px 12px;
  height: 14px;
  &:hover {
    background: #f1f1f6;
    cursor: pointer;
    svg {
      path {
        fill: $dark-grey;
      }
    }
  }
}

.reverse-arrow {
  transform: translateX(-0.9px);
  path {
    transform: rotate(180deg);
    transform-origin: 50% 50%;
  }
}

.sort-by {
  @include font-smaller;
  color: $dark-grey;
  &:hover {
    color: $dark-grey;
  }
}

.selected-sort-button {
  background: $dark-purple;
  svg {
    path {
      fill: white;
    }
  }
  p {
    color: white;
  }
}
.number-sorting {
  position: absolute;
  top: 3px;
  left: 23px;
  font-size: 6px;
  line-height: 7px;
  text-align: center;
  align-items: center;
  width: 10px;
  height: 10px;
  font-weight: 500;
  background-color: $dark-purple;
  color: white;
  padding: 2px 1px 1px 1px;
  border-radius: 50%;
  box-sizing: border-box;
}
.products-with-videos-bar {
  height: 30px;
  width: 100%;
  border: 1px solid $light-grey;
  border-radius: 2px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 8px;
  overflow: hidden;
  p {
    @include font-smaller;
    font-weight: 400;
    flex-grow: 1;
    white-space: nowrap;
  }
  span {
    font-weight: 700;
  }
}
.bar {
  position: relative;
  height: 6px;
  width: 125px;
  border-radius: 4px;
  background-color: #ececec;
}
.filled-bar {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 4px;
  height: 100%;
  background-color: $dark-purple;
  transition: width 0.5s ease;
}
.position-right {
  position: absolute;
  right: 0;
}
.list-element {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}
.list-title {
  @include font-small;
  font-weight: 600;
  white-space: nowrap;
}
.sorting-arrows {
  display: flex;
  flex-direction: column;
  gap: 6px;
  transform: scale(60%);
}

.product-creation-date {
  @include font-smaller;
  font-weight: 400;
}

.status {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.number-of-videos {
  @include font-smaller;
  font-weight: 400;
  background-color: #e2e2e2;
  width: fit-content;
  border-radius: 20px;
  padding: 2px 12px;
  span {
    font-weight: 600;
  }
}

.there-are-videos {
  background-color: #d4f8d3;
}

.add-manage {
  @include font-smaller;
  color: $dark-purple;
  text-decoration: underline;
  cursor: pointer;
}

.last-import {
  @include font-smaller;
  font-weight: 400;
}

.no-widget {
  width: 58.5px;
  height: 35px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fae8ffad;
  border: 2px dashed $dark-purple;
  border-radius: 4px;
  box-sizing: border-box;
  margin-bottom: 2px;
  p {
    font-weight: 600;
    color: $dark-purple;
    border: 2px solid $dark-purple;
    border-radius: 2px;
    width: 10px;
    height: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;
  }
  &:hover {
    background-color: #fae8ff;
    cursor: pointer;
  }
}

.widgets {
  display: flex;
  gap: 8px;
  align-items: center;
}
.widgets-images {
  display: flex;
  gap: 8px;
  align-items: center;
}

.widget-icon-wrapper {
  position: relative;
}

.warning-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.tippy-content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  button {
    width: 50%;
    &:hover {
      background-color: $light-grey;
    }
  }
}

.tooltip-campaign-name {
  font-style: italic;
}

.see-embed-code {
  cursor: pointer;
  padding: 4px 8px;
  white-space: nowrap;
  background-color: white;
  border-radius: 2px;
  color: $dark-purple;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-weight: 500;
}

.selected-arrow {
  path {
    fill: $light-purple;
  }
}
.emotes-tippy-content {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 4px;
  padding: 8px 16px;
  p {
    text-align: start;
    font-weight: 500;
  }
}

.emotes-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.disabled-button {
  color: $light-grey;
  pointer-events: none;
}
.standard-tippy {
  align-items: flex-start;
  gap: 12px;
}

.tippy-content-section {
  display: flex;
  gap: 8px;
  justify-self: flex-start;
}

.tippy-top-block {
  padding: 2px 8px 0 0;
}

.tippy-bottom-block {
  padding-bottom: 6px;
  gap: 16px;
}

.format-type {
  font-weight: bold;
}

.tooltip-icons {
  path {
    fill: $dark-purple;
  }
}

.delete-icon {
  cursor: pointer;
  path {
    fill: rgba(196, 196, 196, 1);
  }
}
.filter-button {
  display: flex;
  gap: 8px;
  align-items: center;
  border: 1px solid $dark-grey;
  border-radius: 2px;
  padding: 4px 12px;
  height: 30px;
  position: relative;
  box-sizing: border-box;
  &:hover {
    background: #f1f1f6;
    cursor: pointer;
    svg {
      path {
        fill: $dark-grey;
      }
    }
  }
}
.selected-filter-button {
  background: $dark-purple;
  svg {
    path {
      fill: white;
    }
  }
  p {
    color: white;
  }
}
.filter-by {
  @include font-smaller;
  color: $dark-grey;
  &:hover {
    color: $dark-grey;
  }
}
.animation-container {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 20px;
}
.lds-ring {
  color: $dark-purple;
}
.lds-ring,
.lds-ring div {
  box-sizing: border-box;
}
.lds-ring {
  display: inline-block;
  position: relative;
  width: 20px;
  height: 20px;
}
.lds-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid currentColor;
  border-radius: 50%;
  animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: currentColor transparent transparent transparent;
  right: 0;
}
.lds-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.lds-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.lds-ring div:nth-child(3) {
  animation-delay: -0.15s;
}
@keyframes lds-ring {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 64px;
  width: 50%;
  position: relative;
  margin-left: 50%;
  transform: translateX(-50%);
}

.sk-folding-cube {
  flex-shrink: 0;
  // width: 80px;
  margin: auto;
  position: relative;
  transform: rotateZ(45deg);

  .sk-cube {
    float: left;
    width: 50%;
    height: 50%;
    position: relative;
    transform: scale(1.1);
    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: $square1-color;
      animation: sk-foldCubeAngle 2.4s infinite linear both;
      transform-origin: 100% 100%;
    }
  }
  .sk-cube2 {
    transform: scale(1.1) rotateZ(90deg);
    &::before {
      background-color: $square2-color;
      animation-delay: 0.3s;
    }
  }
  .sk-cube3 {
    transform: scale(1.1) rotateZ(180deg);
    &::before {
      background-color: $square3-color;
      animation-delay: 0.6s;
    }
  }
  .sk-cube4 {
    transform: scale(1.1) rotateZ(270deg);
    &::before {
      background-color: $square4-color;
      animation-delay: 0.9s;
    }
  }
}
@keyframes sk-foldCubeAngle {
  0%,
  10% {
    transform: perspective(140px) rotateX(-180deg);
    opacity: 0;
  }
  25%,
  75% {
    transform: perspective(140px) rotateX(0deg);
    opacity: 1;
  }
  90%,
  100% {
    transform: perspective(140px) rotateY(180deg);
    opacity: 0;
  }
}

.disabled-sorting {
  cursor: default;
  svg {
    path {
      fill: $light-grey;
    }
  }
}
.number-filters {
  position: absolute;
  left: 20px;
  font-size: 6px;
  line-height: 7px;
  text-align: center;
  align-items: center;
  width: 10px;
  height: 10px;
  font-weight: 500;
  background-color: $dark-purple;
  color: white;
  padding: 2px 1px 1px 1px;
  border-radius: 50%;
  box-sizing: border-box;
}

img[lazy="loading"] {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 35px;
  height: 35px;
}

.refresh-icon {
  height: 30px;
  width: 30px;
}

.language-flag {
  width: 16px;
  height: 16px;
}

.arrow-down-big {
  display: none;
}
.globe-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  width: fit-content;
  padding: 4px 8px;
  cursor: pointer;
  &:hover {
    background-color: rgba(196, 196, 196, 0.5);
    .arrow-down-big {
      display: block;
    }
  }
}
.globe-wrapper-without-hover {
  &:hover {
    background-color: transparent;
  }
}
.product-localization-pink-background {
  background-color: rgba(226, 124, 252, 0.1);
}
</style>
